import React, { useState, useEffect, useCallback, useContext } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { colors } from '@com/core/noon/styles/noonTheme';
import { MARKETPLACE_CONFIG } from '@com/core/common/utils/config';
import ToastContext from '@com/core/common/contexts/toastContext';
import { MapContext } from '@com/core/common/contexts/mapContext';
import { useTranslation } from '@com/core/common/translations/i18n';
import { countrySVC, customerSVC } from '@com/core/common/services/api';
import MaterialInput from '@com/core/common/components/MaterialInput';
import { OptionTypeBase } from 'react-select';
import { CustomerContext } from '@com/core/common/contexts/customerContext';
import { CartContext } from '@com/core/common/contexts/cartContextDaily';
import validator from '@com/core/common/utils/validator';
import { TAddress, TAddressesPOST, TCountryPhoneCode } from '@com/core/common/types/customer_address';
import isEmpty from 'lodash/isEmpty';

import Input from '@com/core/noon/components/Input';
import { redirect, TApp } from '@com/core/common/utils/url';
import { TCountry } from '@com/core/common/types/locale';
import { AppContext } from '@com/core/common/contexts/appContext';
import { values } from 'lodash';
import { DailyContext } from '@com/core/common/contexts/dailyContext';
import AccountLayout from '../common/AccountLayout';
import BlockButton from '../common/BlockButton';
import CountryCodeSelector from './CountryCodeSelector';
import AddressPhoneVerifyModal from '../common/AddressPhoneVerifyModal';

type TProps = {
    editAddressObj: TAddress;
    formRole: 'add' | 'edit';
    switchView: (view: 'list' | 'mapBrowse') => void;
};

type TFormErrors = {
    firstName?: string;
    lastName?: string;
    address?: string;
    phone?: string;
    phoneAreaCode?: string;
    country?: string;
    city?: string;
    area?: string;
    streetAddress?: string;
};

type TFormData = {
    // address: string;
    firstName: string;
    lastName: string;
    phone: string;
    phoneCountryCode: string;
    phoneAreaCode: string;
    streetAddress: string;
};

type TPhone = {
    extractedCountry: string;
    extractedArea?: string;
    extractedPhoneNumber: string;
};

const AddressForm = ({ editAddressObj, formRole, switchView }: TProps): JSX.Element => {
    const { t } = useTranslation('common');
    const { locale, webConfig } = useContext(AppContext);
    const { showToast } = useContext(ToastContext);
    const { removeClickedLocation } = useContext(MapContext);
    const { addresses, customer } = useContext(CustomerContext);
    const { checkAddress } = useContext(DailyContext);
    const { addToCart, getCart } = useContext(CartContext);
    const [currentAddress, setCurrentAddress] = useState(null);
    const [showPhoneVerify, setShowPhoneVerify] = useState(false);
    const { query } = useRouter();
    const qRedirect = query.redirect as TApp;
    const qPath = query.path as string;
    const editAddress = editAddressObj;
    const isCountryCodeChangeEnabled = webConfig.country.is_address_international_phone_enabled;

    const isLegacyDialCode = (dialCode: string): boolean => {
        return dialCode && ['+971', '+966', '+20'].includes(dialCode);
    };

    const extractedPhoneDetails = (phone: string): TPhone => {
        let extractedCountry = '';
        let extractedArea = '';
        let extractedPhoneNumber = '';
        if (!phone) {
            return { extractedCountry, extractedArea, extractedPhoneNumber };
        }
        const [code] = phone && phone.split('-');
        if (isLegacyDialCode(code)) {
            [extractedCountry, extractedArea, extractedPhoneNumber] = phone.split('-') || ['', '', ''];
            return { extractedCountry, extractedArea, extractedPhoneNumber };
        }
        [extractedCountry, extractedPhoneNumber] = phone.split('-') || ['', ''];
        return { extractedCountry, extractedPhoneNumber };
    };
    const {
        extractedArea: extractedAreaCode,
        extractedCountry: extractedCountryCode,
        extractedPhoneNumber: extractedPhone,
    } = extractedPhoneDetails(editAddress.phone);

    const getSelectedCountry = (role: 'edit', country?: string): TCountryPhoneCode => {
        let pickedCountry = {} as TCountryPhoneCode | null;
        try {
            if (role === 'edit' && country && isCountryCodeChangeEnabled) {
                pickedCountry = country && webConfig.country_phone_codes.find(value => value.dial_code === country);
                const area_codes = pickedCountry.area_codes.map(data => ({ label: data, value: data }));
                return { ...pickedCountry, area_codes };
            }
        } catch (error) {
            showToast(error, 'To edit address get selected country');
        }

        return null;
    };

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [countryData, setCountryData] = useState<TCountry | null>(null);
    const [areaCodeOptions, setAreaCodeOptions] = useState<OptionTypeBase[]>([]);
    const [showCountryCodeSelector, setShowCountryCodeSelector] = useState<boolean>(false);
    const [selectedCountryCode, setSelectedCountryCode] = useState<TCountryPhoneCode>(
        getSelectedCountry('edit', extractedCountryCode || webConfig.country.dial_code),
    );

    const [formData, setFormData] = useState<TFormData>({
        firstName: editAddress.firstName || customer?.firstName || '',
        lastName: editAddress.lastName || customer?.lastName || '',
        phone: extractedPhone,
        phoneCountryCode: extractedCountryCode,
        phoneAreaCode: extractedAreaCode,
        streetAddress: editAddress.streetAddress,
    });
    const [formErrors, setFormErrors] = useState<TFormErrors>({});

    const countryCode = locale.apiRegion.toUpperCase();
    const showMap = (addressObj = editAddress): boolean => !!addressObj.lat && !!addressObj.lng;

    const getData = useCallback(async () => {
        try {
            countrySVC(locale.apiLocale)
                .getCountryData(countryCode)
                .then((data: any) => {
                    setCountryData(data.data);
                    if (formRole === 'edit') {
                        if (extractedAreaCode && isCountryCodeChangeEnabled && webConfig.country_phone_codes?.length) {
                            setAreaCodeOptions(
                                extractedAreaCode ? getSelectedCountry('edit', extractedCountryCode)?.area_codes : [],
                            );
                        } else {
                            setAreaCodeOptions(
                                extractedAreaCode
                                    ? data.data.phone_area_code.map((areaCode: string) => ({
                                          label: areaCode,
                                          value: areaCode,
                                      }))
                                    : [],
                            );
                        }
                    } else {
                        setAreaCodeOptions(
                            data.data.phone_area_code.map((areaCode: any) => ({
                                label: areaCode,
                                value: areaCode,
                            })),
                        );
                        setFormData({ ...formData, phoneAreaCode: data.data.phone_area_code[0] || '' });
                    }
                    setIsLoading(false);
                });
        } catch (err) {
            showToast('success', err.error);
            setError(err.error);
            setIsLoading(false);
        }
    }, [locale, showToast, countryCode]);

    useEffect(() => {
        getData();
    }, [getData, customer]);

    const validateForm = async (
        key:
            | 'all'
            | 'firstName'
            | 'lastName'
            | 'phone'
            | 'address'
            | 'phoneAreaCode'
            | 'recipientName'
            | 'recipientPhone'
            | 'recipientPhoneAreaCode'
            | 'streetAddress',
        value?: string,
        countryCodeData?: TCountryPhoneCode,
    ): Promise<boolean> => {
        const errors = formErrors;

        if (key === 'all' || key === 'firstName') {
            errors.firstName = '';
            if (!validator.isValidInput(value || formData.firstName)) {
                errors.firstName = t('_address_form_validation_valid_name');
            }
            // validation check for too long name(first and lastname is limit to 100 chars)
            if (validator.isLongName(value || formData.firstName)) {
                errors.firstName = t('_address_form_validation_long_input_error');
            }
            // validation check for names starting with special charactors and numbers
            if (!validator.isValidName(value || formData.firstName)) {
                errors.firstName = t('_address_form_validation_valid_name');
            }
            if (!validator.isPotentiallyValidName(value || formData.firstName)) {
                errors.firstName = t('_address_form_validation_valid_name');
            }
            if (!validator.isAlphanumeric(value || formData.firstName)) {
                errors.firstName = t('_address_form_validation_valid_special_char');
            }
            if (validator.isEmpty(value !== undefined ? value : formData.firstName)) {
                errors.firstName = t('error-fistName-empty');
            }
        }

        if (key === 'all' || key === 'lastName') {
            errors.lastName = '';
            if (!validator.isValidInput(value || formData.lastName)) {
                errors.lastName = t('_address_form_validation_valid_name');
            }
            if (validator.isLongName(value || formData.lastName)) {
                errors.lastName = t('_address_form_validation_long_input_error');
            }
            if (!validator.isValidName(value || formData.lastName)) {
                errors.lastName = t('_address_form_validation_valid_name');
            }
            if (!validator.isPotentiallyValidName(value || formData.lastName)) {
                errors.lastName = t('_address_form_validation_valid_name');
            }
            if (!validator.isAlphanumeric(value || formData.lastName)) {
                errors.lastName = t('_address_form_validation_valid_special_char');
            }
            if (validator.isEmpty(value !== undefined ? value : formData.lastName)) {
                errors.lastName = t('error-lastName-empty');
            }
        }

        if (key === 'all' || key === 'streetAddress') {
            errors.streetAddress = '';
            if (validator.isEmpty(value !== undefined ? value : formData.streetAddress)) {
                errors.streetAddress = t('field-required');
            }
        }

        // if (clickedLocation && !selectedCollectionPoint && (key === 'all' || key === 'address')) {
        //     if (!validator.isValidInput(value || formData.address)) {
        //         errors.address = t('_address_form_validation_valid_address');
        //     }
        //     if (validator.isLongAddress(value || formData.address)) {
        //         errors.address = t('_address_form_validation_long_input_error');
        //     }
        //     if (validator.isShortAddress(value || formData.address)) {
        //         errors.address = t('_address_form_validation_short_input_error');
        //     }
        //     if (!validator.isPotentiallyValidAddress(value || formData.address)) {
        //         errors.address = t('_address_form_validation_valid_address');
        //     }
        //     if (!validator.isValidAddress(value || formData.address)) {
        //         errors.address = t('_address_form_validation_valid_name');
        //     }
        // }

        if (key === 'all' || key === 'phone' || key === 'phoneAreaCode') {
            errors.phone = '';
            // const phoneError = t(`_address_form_validation_valid_phone_${locale.apiRegion}`);
            const phoneError = t('enter_valid_phone_number');

            let validPhone = '';
            if (key === 'phoneAreaCode') {
                validPhone = value + formData.phone;
            } else if (key === 'phone') {
                validPhone = (formData.phoneAreaCode || '') + value;
                if (areaCodeOptions?.length > 0 && formData.phoneAreaCode === '') {
                    errors.phone = phoneError;
                }
            }

            if (value === '' && key === 'phone') {
                errors.phone = '';
                return values(errors).every(isEmpty);
            }

            if (
                !validator.isValidPhone(
                    validPhone || (formData.phoneAreaCode || '') + formData.phone,
                    locale.apiRegion,
                    isCountryCodeChangeEnabled ? countryCodeData || selectedCountryCode : null,
                )
            ) {
                errors.phone = phoneError;
            }
            if (
                !validator.isValidInput(value || formData.phoneAreaCode) &&
                (areaCodeOptions?.length > 0 || countryCodeData?.area_codes.length > 0)
            ) {
                errors.phone = phoneError;
            }
            if (
                !validator.isPotentiallyValidPhone(
                    validPhone || (formData.phoneAreaCode || '') + formData.phone,
                    locale.apiRegion,
                    isCountryCodeChangeEnabled ? countryCodeData || selectedCountryCode : null,
                )
            ) {
                errors.phone = phoneError;
            }
        }
        setFormErrors(errors);
        return values(errors).every(isEmpty);
    };

    const handleFormUpdate = (key: any, value?: string, countryCodeData?: TCountryPhoneCode): void => {
        if (key === 'phone') {
            setFormData({ ...formData, [key]: value?.replace(/\s/g, '') });
            validateForm(key, value?.replace(/\s/g, ''), countryCodeData);
        } else {
            setFormData({ ...formData, [key]: value });
            validateForm(key, value, countryCodeData);
        }
    };

    const handleSaveAddress = async (): Promise<void> => {
        if (await validateForm('all')) {
            const initialAddressObj: TAddressesPOST = {
                area: editAddressObj ? editAddressObj.area : '',
                firstName: formData.firstName,
                id_city: editAddressObj ? editAddressObj.city.id : 0,
                lastName: formData.lastName,
                lat: editAddressObj ? editAddressObj.lat : 0,
                lng: editAddressObj ? editAddressObj.lng : 0,
                phone: `${selectedCountryCode?.dial_code || countryData?.dial_code}${
                    formData.phoneAreaCode ? `-${formData.phoneAreaCode}` : ''
                }-${formData.phone}`,
                thumbnail: editAddressObj ? editAddressObj.thumbnail : '',
                type: 'latlng',
                streetAddress: formData.streetAddress,
                city: {
                    id: editAddressObj ? editAddressObj.city.id : 0,
                    name: editAddressObj ? editAddressObj.city.name : '',
                },
                countryCode: selectedCountryCode?.dial_code || extractedCountryCode,
            };

            setIsLoading(true);

            try {
                const { data } =
                    formRole === 'add'
                        ? await customerSVC(locale.apiLocale).address.post(initialAddressObj)
                        : await customerSVC(locale.apiLocale).address.put(editAddress.id.toString(), initialAddressObj);

                if (data.error) {
                    setError(data.error);
                    showToast('error', JSON.stringify(data.error), 'error');
                    setIsLoading(false);
                } else if (qRedirect) {
                    redirect(locale.isoLocale, qPath, qRedirect);
                } else {
                    showToast(
                        'success',
                        formRole === 'add' ? t('_address_add_address_success') : t('_address_edit_address_success'),
                    );
                    removeClickedLocation();
                    if (addresses.isQuickAddToCart) {
                        // this is a one off, only for new users without an address
                        getCart(false);
                        checkAddress();
                        const primaryAddress = () => data.data?.find(addrs => addrs.defaultShipping);

                        if (primaryAddress()?.isPhoneVerified) {
                            const {
                                attachments,
                                eventLocation,
                                offerCode,
                                quantity,
                                showModal,
                                sku,
                            } = addresses.productToAdd;
                            addToCart(offerCode, quantity, sku, showModal, eventLocation, attachments);
                            addresses.toggleShowAddressModal(false, false); // also marks isQuickAddToCart as false
                        } else {
                            setShowPhoneVerify(true);
                        }
                    } else {
                        switchView('list');
                    }
                    setCurrentAddress(data.data[0]);
                    setIsLoading(false);
                }
            } catch (err) {
                showToast('error', err.error || t('something-went-wrong'));
                setError(err.error || t('something-went-wrong'));
            }
        }
    };
    const isEditing = formRole === 'edit';

    const isSaveDisabled = () => {
        if (areaCodeOptions?.length > 0) {
            return (
                isEmpty(formData.firstName) ||
                isEmpty(formData.lastName) ||
                isEmpty(formData.phone) ||
                isEmpty(formData.phoneAreaCode) ||
                isEmpty(formData.streetAddress)
            );
        }
        return (
            isEmpty(formData.firstName) ||
            isEmpty(formData.lastName) ||
            isEmpty(formData.phone) ||
            isEmpty(formData.streetAddress)
        );
    };

    const isAreaCodesAvailable =
        (formRole === 'edit' && (!!areaCodeOptions?.length || formData.phoneAreaCode)) || formRole === 'add';

    return (
        <AccountLayout titleKey="" section="addresses" subtitleKey="" isLoading={isLoading} error={error}>
            <>
                <FormOuterContainer id="FormOuterContainer">
                    {isEditing ? <h2>{t('edit-address')}</h2> : <h2>{t('add-new-address')}</h2>}
                    {!isEditing && <p className="confimAddress">{t('add_address_confirm')}</p>}
                    <Panel>
                        <FormRowSection>
                            <FormColumn>
                                <FormHeading>
                                    {t('_address_form_location_title')}
                                    <img
                                        src={MARKETPLACE_CONFIG.assets.images.flags[locale.apiRegion]}
                                        alt={`${locale.apiRegion}_flag`}
                                    />
                                </FormHeading>

                                {editAddressObj && showMap && (
                                    <LocationOverview>
                                        <p className="areaInfo">
                                            {editAddressObj.area}
                                            <span className="city">{editAddressObj.city?.name}</span>
                                        </p>
                                        <button
                                            className="thumbnailContainer"
                                            type="button"
                                            onClick={() => switchView('mapBrowse')}>
                                            <img src={editAddressObj.thumbnail} alt="mapThumbnail" />
                                            <span>{t('change')}</span>
                                        </button>
                                    </LocationOverview>
                                )}
                                <StreetAddress>
                                    <Input
                                        required
                                        placeholder={t('please_enter_address_details')}
                                        id="streetAddress"
                                        type="text"
                                        label={t('_address_form_additional_address_label')}
                                        onChange={e => {
                                            handleFormUpdate('streetAddress', e.target.value);
                                        }}
                                        value={formData.streetAddress}
                                        error={formErrors.streetAddress}
                                    />
                                </StreetAddress>
                            </FormColumn>

                            <FormColumn>
                                <FormHeading>{t('_address_form_personal_info_title')}</FormHeading>
                                <FormItem>
                                    <CountryPhoneNumber>{t('_address_form_phone_placeholder')}*</CountryPhoneNumber>

                                    <PhoneDetailsContainer>
                                        <CountryAreaCodeContainer>
                                            <CountryCodeContainer isAreaCodeAvailable={!!areaCodeOptions?.length}>
                                                {locale.isUAE && isCountryCodeChangeEnabled ? (
                                                    <CountryCode
                                                        isAreaCodeAvailable={!!areaCodeOptions?.length}
                                                        isDisabled={!isCountryCodeChangeEnabled}
                                                        onClick={() => setShowCountryCodeSelector(true)}>
                                                        <p>
                                                            {' '}
                                                            {selectedCountryCode ? selectedCountryCode.dial_code : ''}
                                                        </p>
                                                        {isCountryCodeChangeEnabled && (
                                                            <CaretDown>
                                                                <img
                                                                    src="/simpleIcons/chevron-down.svg"
                                                                    alt="areaCode"
                                                                />
                                                            </CaretDown>
                                                        )}
                                                    </CountryCode>
                                                ) : (
                                                    // <Input
                                                    //     id="phoneCountryCode"
                                                    //     type="text"
                                                    //     value={
                                                    //         formData.phoneCountryCode || (countryData || {}).dial_code || ''
                                                    //     }
                                                    //     onChange={() => {
                                                    //         /* Leaving Empty for TS */
                                                    //     }}
                                                    //     label=""
                                                    //     className="countryDisabled"
                                                    //     required
                                                    // />
                                                    <p className="countryDisabled">
                                                        {formData.phoneCountryCode ||
                                                            (countryData || {}).dial_code ||
                                                            ''}
                                                    </p>
                                                )}
                                            </CountryCodeContainer>
                                            {areaCodeOptions?.length > 0 && isAreaCodesAvailable && (
                                                <AreaCodeContainer>
                                                    <MaterialInput
                                                        required
                                                        select
                                                        onChange={e => {
                                                            handleFormUpdate('phoneAreaCode', e.target.value);
                                                        }}
                                                        value={formData.phoneAreaCode}
                                                        options={areaCodeOptions}
                                                    />
                                                </AreaCodeContainer>
                                            )}
                                        </CountryAreaCodeContainer>
                                        <PhoneContainer>
                                            <Input
                                                placeholder={t('enter_mobile_number')}
                                                id="phone"
                                                type="text"
                                                label=""
                                                onChange={e => {
                                                    handleFormUpdate('phone', e.target.value);
                                                }}
                                                value={formData.phone}
                                                error={formErrors.phone}
                                            />
                                        </PhoneContainer>
                                        {showCountryCodeSelector && (
                                            <CountryCodeSelector
                                                selectedCountryCode={selectedCountryCode}
                                                setSelectedCountryCode={selectedCode => {
                                                    if (selectedCode) {
                                                        setSelectedCountryCode(selectedCode);
                                                        setShowCountryCodeSelector(false);
                                                        setAreaCodeOptions(selectedCode.area_codes);
                                                        handleFormUpdate(
                                                            'phoneAreaCode',
                                                            selectedCode.area_codes[0]?.value || '',
                                                            selectedCode,
                                                        );
                                                    } else {
                                                        setShowCountryCodeSelector(false);
                                                    }
                                                }}
                                            />
                                        )}
                                    </PhoneDetailsContainer>
                                </FormItem>
                                <FormItem>
                                    <div>
                                        <Input
                                            required
                                            placeholder={t('please_enter_first_name')}
                                            id="firstName"
                                            type="text"
                                            label={t('first-name')}
                                            onChange={e => {
                                                handleFormUpdate('firstName', e.target.value);
                                            }}
                                            value={formData.firstName}
                                            error={formErrors.firstName}
                                        />
                                    </div>
                                </FormItem>

                                <FormItem>
                                    <div>
                                        <Input
                                            required
                                            placeholder={t('please_enter_last_name')}
                                            id="lastName"
                                            type="text"
                                            label={t('last-name')}
                                            onChange={e => {
                                                handleFormUpdate('lastName', e.target.value);
                                            }}
                                            value={formData.lastName}
                                            error={formErrors.lastName}
                                        />
                                    </div>
                                </FormItem>
                            </FormColumn>
                        </FormRowSection>
                        <ActionContainer>
                            <div>
                                <BlockButton
                                    onClick={(): void => {
                                        handleSaveAddress();
                                    }}
                                    disabled={isSaveDisabled() || !values(formErrors).every(isEmpty)}
                                    variant="primary">
                                    {t('_address_form_save-address')}
                                </BlockButton>
                            </div>
                            <div>
                                <BlockButton
                                    onClick={(): void => {
                                        switchView('list');
                                    }}
                                    variant="tertiary">
                                    {t('back')}
                                </BlockButton>
                            </div>
                        </ActionContainer>
                    </Panel>
                </FormOuterContainer>
                {showPhoneVerify ? (
                    <PhoneVerifyModal>
                        <AddressPhoneVerifyModal
                            toggle={(): void => {
                                setShowPhoneVerify(show => !show);
                            }}
                            successCallback={(): void => {
                                const {
                                    attachments,
                                    eventLocation,
                                    offerCode,
                                    quantity,
                                    showModal,
                                    sku,
                                } = addresses.productToAdd;
                                addToCart(offerCode, quantity, sku, showModal, eventLocation, attachments);
                                addresses.toggleShowAddressModal(false, false); // also marks isQuickAddToCart as false
                                setShowPhoneVerify(false);
                            }}
                            address={currentAddress || null}
                        />
                        ;
                    </PhoneVerifyModal>
                ) : null}
            </>
        </AccountLayout>
    );
};

const PhoneVerifyModal = styled.div`
    position: fixed;
    z-index: 10;
    top: 0;
    bottom: 0;
    background-color: ${({ theme }): string => theme.colors.bgPrimary};
`;

const CountryPhoneNumber = styled.label`
    color: ${colors.bodyMid};
    font-size: 1rem;
    max-width: 100%;
    pointer-events: none;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    transition: all 0.2s ease 0s;
`;

const CaretDown = styled.div`
    width: 20px;
    height: 20px;
    display: 'flex';
    ${({ theme: { left } }): string => `margin-${left}: 10px;`}

    & > img {
        margin: 0 auto;
    }
`;

const StreetAddress = styled.div`
    margin-top: 20px;
`;

const CountryCode = styled.div<{ isDisabled: boolean; isAreaCodeAvailable: boolean }>`
    flex: none;
    display: flex;
    ${({ theme: { right } }): string => `margin-${right}: 5px;`}
    border-right: ${({ isAreaCodeAvailable, theme }): string =>
        isAreaCodeAvailable ? `1px solid  ${theme.colors.border}` : 'none'};
    color: ${({ isDisabled, theme }): string => (isDisabled ? theme.colors.bodyLight : theme.colors.body)};
    cursor: default;
    padding: 0 10px;
    align-items: center;
`;

const FormOuterContainer = styled.div`
    h2 {
        font-size: 1.375rem;
        font-weight: bold;
        margin-bottom: 16px;
    }
    .confimAddress {
        color: #7e859b;
        font-size: 0.875rem;
        margin-bottom: 20px;
    }
    .fauxInputContainer {
        .label {
            color: ${({ theme }): string => theme.colors.bodyLight};
            font-size: 0.875rem;
            margin-bottom: 6px;
        }

        .fauxInput {
            display: flex;
            align-items: center;
            border-radius: ${({ theme }): string => theme.metrics.borderRadius};
            background-color: ${({ theme }): string => theme.colors.bgSecondary};
            height: 48px;
            padding: 0 16px;

            .value {
                flex: 1;
                color: ${({ theme }): string => theme.colors.bodyLight};
                cursor: not-allowed;
            }

            button {
                ${({ theme: { left } }): string => `margin-${left}: 12px;`}
                font-size: 0.875rem;

                &:hover {
                    text-decoration: underline;
                }
            }
        }
    }
`;

const Panel = styled.div`
    border: 1px solid ${({ theme }): string => theme.colors.border};
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    padding: 24px 24px 28px;

    .Select-control {
        border: 0;
        border-radius: 0;
    }

    .is-focused:not(.is-open) > .Select-control {
        border: 0;
        border-radius: 0;
        box-shadow: none;
    }

    .Select-value-label {
        font-size: 1.3333rem;
        font-weight: 600;
        position: relative;
    }

    .Select-input {
        font-size: 1.3333rem;
    }
`;

const FormRowSection = styled.div`
    display: flex;
`;

const FormColumn = styled.div`
    flex: 1;

    input {
        border: none;
        background-color: #f7f7fa;
        border-radius: 2px;
        height: 47px;
        ${({ theme: { left } }): string => `padding-${left}: 5px;`};
    }
    label {
        padding-bottom: 14px;
        display: inline-block;
    }
    input:focus, input: active {
        border: 1px solid ${({ theme }): string => theme.colors.bodyLight};
    }
    .MuiInput-underline:before,
    .MuiInput-underline:after {
        border-bottom: none;
    }
    .MuiSelect-select.MuiSelect-select {
        ${({ theme: { left } }): string => `padding-${left}: 5px;`}
        padding-top: 10px;
    }
    .MuiSelect-select:focus {
        background-color: #f7f7fa;
    }
    .MuiInput-underline:hover:not(.Mui-disabled):before {
        border: none;
    }
    & + & {
        ${({ theme: { left } }): string => `margin-${left}: 24px;`}
    }
`;

const FormHeading = styled.h3`
    display: flex;
    align-items: center;
    font-weight: 600;
    margin-bottom: 24px;

    img {
        ${({ theme: { left } }): string => `margin-${left}: 8px;`}
    }
`;

const LocationOverview = styled.div`
    border: 1px solid ${({ theme }): string => theme.colors.borderLight};
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    display: flex;
    padding: 16px;
    margin-bottom: 16px;

    p.areaInfo {
        flex: 1;
        ${({ theme: { right } }): string => `margin-${right}: 16px;`}

        font-weight: 600;

        span {
            display: block;
            font-size: 0.9375rem;
            font-weight: normal;
        }
    }

    button.thumbnailContainer {
        position: relative;
        border-radius: 2px;
        overflow: hidden;

        &:hover {
            opacity: 0.7;
        }

        img {
            width: 64px;
        }

        span {
            font-size: 0.75rem;
            font-weight: 600;
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            background-color: rgba(0, 0, 0, 0.2);
            color: ${({ theme }): string => theme.colors.bodyAlt};
            padding: 2px 0;
        }
    }
`;

const FormItem = styled.div`
    margin-bottom: 20px;
    position: relative;
`;

const PhoneDetailsContainer = styled.div`
    position: relative;
    display: flex;
    ${({ theme }): string => (theme.isRTL ? `direction: ltr;` : '')};
`;

const CountryAreaCodeContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    height: 100%;
    /* width: 120px; */

    .MuiSelect-selectMenu {
        min-height: 1.1rem;
    }
`;

const CountryCodeContainer = styled.div<{ isAreaCodeAvailable: boolean }>`
    /* width: 44px; */
    z-index: 10;
    flex: 1;
    height: 47px;
    margin-bottom: 5px;
    align-items: center;
    display: flex;
    justify-content: center;
    background-color: #f7f7fa;
    border-radius: 2px;

    .countryDisabled {
        pointer-events: none;
        user-select: none;
        width: 60px;
        margin-top: 4px;
        text-align: center;
        padding: 0 5px;
        border-right: ${({ isAreaCodeAvailable, theme }): string =>
            isAreaCodeAvailable ? `1px solid ${theme.colors.border}` : 'unset'};
        height: 25px;
        border-radius: unset;
    }
    label {
        visibility: hidden;
    }
`;

const AreaCodeContainer = styled.div`
    width: 60px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 5px;
    z-index: 9;
    flex: 1;
    height: 47px;
    border: none;
    background-color: #f7f7fa;
    border-radius: 0;
`;

const PhoneContainer = styled.div`
    flex: 1;
    margin-top: 0;
    ${({ theme: { left } }): string => `margin-${left}: 15px;`}

    label {
        visibility: hidden;
        padding: 0;
        display: initial;
    }
`;

// const NotVerified = styled.span`
//     color: ${({ theme }): string => theme.colors.generalWarning};
//     padding: 0 10px;
//     visibility: hidden;
// `;

const ActionContainer = styled.div`
    margin-top: 32px;
    justify-content: flex-end;
    display: flex;
    align-items: center;

    > div + div {
        ${({ theme: { left } }): string => `margin-${left}: 12px;`}
    }
`;

export default AddressForm;
